'use client';

import { useState } from 'react';
import type { Activity } from '@/data/activities';
import SmartImage from '@/components/ui/SmartImage';
import SectionHeading from '@/components/ui/SectionHeading';
import {
  Sparkles, Mountain, Dog, Gauge, TreePine, Footprints,
  Bike, Sun, Waves, Cherry, Fish, Target, Flame,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

const iconMap: Record<string, LucideIcon> = {
  Sparkles, Mountain, Dog, Gauge, TreePine, Footprints,
  Bike, Sun, Waves, Cherry, Fish, Target, Flame,
};

const resortLabel: Record<string, string> = {
  levi: 'Levi',
  yllas: 'Ylläs',
  both: 'Levi & Ylläs',
};

function truncateToSentences(text: string, max: number): string {
  const sentences = text.match(/[^.!]+[.!]+/g) || [text];
  return sentences.slice(0, max).join(' ').trim();
}

interface ThingsToDoCopy {
  title: string;
  intro: string;
  sectionTitles: {
    winter: string;
    summer: string;
    yearRound: string;
  };
  card: {
    where: string;
    when: string;
    flipBack: string;
  };
  seasonLabels: {
    winter: string;
    summer: string;
    yearRound: string;
  };
}

const seasonKey: Record<string, keyof ThingsToDoCopy['seasonLabels']> = {
  winter: 'winter',
  summer: 'summer',
  both: 'yearRound',
};

function ActivityCard({
  activity,
  card,
  seasonLabels,
}: {
  activity: Activity;
  card: ThingsToDoCopy['card'];
  seasonLabels: ThingsToDoCopy['seasonLabels'];
}) {
  const [flipped, setFlipped] = useState(false);
  const image = activity.image.winter || activity.image.summer || '';
  const Icon = iconMap[activity.icon];

  return (
    <div
      className="cursor-pointer [perspective:1000px]"
      style={{ aspectRatio: '3/4' }}
      onClick={() => setFlipped(!flipped)}
    >
      <div
        className="relative w-full h-full transition-transform duration-[400ms] ease-in-out [transform-style:preserve-3d]"
        style={{ transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)' }}
      >
        {/* Front */}
        <div className="absolute inset-0 [backface-visibility:hidden] rounded-xl overflow-hidden shadow-sm">
          {image ? (
            <SmartImage
              src={image}
              alt={activity.name}
              width={600}
              height={800}
              fill
              className="object-cover"
            />
          ) : (
            <div className="w-full h-full bg-[#F0EDE5]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-5">
            {Icon && (
              <div className="w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-2">
                <Icon size={18} className="text-white" />
              </div>
            )}
            <h3 className="font-sans text-xl font-light text-white">
              {activity.name}
            </h3>
          </div>
        </div>

        {/* Back */}
        <div
          className="absolute inset-0 [backface-visibility:hidden] rounded-xl overflow-hidden shadow-sm bg-white p-5 flex flex-col"
          style={{ transform: 'rotateY(180deg)' }}
        >
          <h3 className="font-sans text-lg font-light text-[#1A1A2E] mb-3">
            {activity.name}
          </h3>

          <p className="text-sm leading-relaxed text-[#5A5A6E] mb-4 flex-1">
            {truncateToSentences(activity.fullDescription, 4)}
          </p>

          <div className="space-y-1.5 text-xs text-[#5A5A6E] border-t border-[#F0EDE5] pt-3 mt-auto">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-[#1A1A2E]">{card.where}</span>
              <span>{resortLabel[activity.resort]}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-[#1A1A2E]">{card.when}</span>
              <span>{seasonLabels[seasonKey[activity.season]]}</span>
            </div>
          </div>

          <p className="text-xs text-[#9B7B3A] mt-3">
            {card.flipBack}
          </p>
        </div>
      </div>
    </div>
  );
}

function ActivitySection({
  title,
  items,
  card,
  seasonLabels,
}: {
  title: string;
  items: Activity[];
  card: ThingsToDoCopy['card'];
  seasonLabels: ThingsToDoCopy['seasonLabels'];
}) {
  if (items.length === 0) return null;
  return (
    <div className="mb-16">
      <h2 className="font-sans text-2xl font-light mb-8 text-[#1A1A2E]">
        {title}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((activity) => (
          <ActivityCard key={activity.slug} activity={activity} card={card} seasonLabels={seasonLabels} />
        ))}
      </div>
    </div>
  );
}

export default function ThingsToDoPage({ items, copy }: { items: Activity[]; copy: ThingsToDoCopy }) {
  const winterActivities = items.filter((a) => a.season === 'winter');
  const summerActivities = items.filter((a) => a.season === 'summer');
  const yearRoundActivities = items.filter((a) => a.season === 'both');

  return (
    <main className="bg-[#FAFAF5] text-[#1A1A2E]">
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            title={copy.title}
            as="h1"
            className="mb-6"
          />
          <p className="text-center text-[#5A5A6E] max-w-3xl mx-auto mb-16 leading-relaxed">
            {copy.intro}
          </p>

          <ActivitySection title={copy.sectionTitles.winter} items={winterActivities} card={copy.card} seasonLabels={copy.seasonLabels} />
          <ActivitySection title={copy.sectionTitles.summer} items={summerActivities} card={copy.card} seasonLabels={copy.seasonLabels} />
          <ActivitySection title={copy.sectionTitles.yearRound} items={yearRoundActivities} card={copy.card} seasonLabels={copy.seasonLabels} />
        </div>
      </section>
    </main>
  );
}
